import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  DotSpinner,
} from '@crono-one/design-system';
import cn from 'clsx';
import { useDebouncedValue } from 'crono-fe-common/hooks/useDebouncedValue';
import { ReactNode, useEffect, useState } from 'react';
import { SEARCH_EDIT_REASONS, useLoadMoreSentinel } from './shared';

export interface InfiniteComboboxProps<T> {
  value: T | null;
  onChange: (value: T | null) => void;
  options: T[];
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  renderOption?: (option: T) => ReactNode;
  // Fired with the debounced input text.
  onSearchChange: (search: string) => void;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  empty?: ReactNode;
  loadingLabel?: string;
  loadingMoreLabel?: string;
  loading?: ReactNode;
  loadingMore?: ReactNode;
  debounceMs?: number;
  disabled?: boolean;
  dsSize?: 'sm' | 'md';
  positionerClassname?: string;
  contentClassName?: string;
}

// Server-paginated + infinite-scroll Combobox with debounced server-search.
const InfiniteCombobox = <T,>({
  value,
  onChange,
  options,
  getOptionKey,
  getOptionLabel,
  renderOption,
  onSearchChange,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  placeholder = 'Search...',
  empty = 'No results',
  loadingLabel = 'Loading...',
  loadingMoreLabel = loadingLabel,
  loading = <Loading label={loadingLabel} />,
  loadingMore = <Loading label={loadingMoreLabel} className="py-2 text-sm" />,
  debounceMs = 500,
  disabled,
  dsSize,
  positionerClassname,
  contentClassName,
}: InfiniteComboboxProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebouncedValue(inputValue, debounceMs);

  useEffect(() => {
    onSearchChange(debouncedSearch);
  }, [debouncedSearch, onSearchChange]);

  const setSentinel = useLoadMoreSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <Combobox
      items={options}
      value={value}
      onValueChange={onChange}
      onInputValueChange={(next, details) => {
        if (SEARCH_EDIT_REASONS.includes(details.reason)) {
          setInputValue(next);
        }
      }}
      itemToStringLabel={getOptionLabel}
      isItemEqualToValue={(a: T, b: T) => getOptionKey(a) === getOptionKey(b)}
      filter={() => true}
      disabled={disabled}
    >
      <ComboboxInput placeholder={placeholder} dsSize={dsSize} showClear />
      <ComboboxContent
        positionerClassname={positionerClassname}
        className={contentClassName}
      >
        <ComboboxEmpty>{isLoading ? loading : empty}</ComboboxEmpty>
        <ComboboxList>
          {options.map((item: T) => (
            <ComboboxItem key={getOptionKey(item)} value={item}>
              {renderOption ? renderOption(item) : getOptionLabel(item)}
            </ComboboxItem>
          ))}
          {hasNextPage && <div ref={setSentinel} className="h-px" />}
        </ComboboxList>
        {isFetchingNextPage && loadingMore}
      </ComboboxContent>
    </Combobox>
  );
};

interface LoadingProps {
  label: string;
  className?: string;
}

const Loading = ({ label, className }: LoadingProps) => (
  <div
    className={cn(
      'flex justify-center items-center gap-2 text-sky-700',
      className,
    )}
  >
    {label} <DotSpinner className="size-5" />
  </div>
);

export default InfiniteCombobox;
